import { useMutation } from "@tanstack/react-query";
import { DateInput } from "components/DateInput";
import { Dialog } from "components/Dialog";
import { LoadingSection } from "components/LoadingSection";
import { errorReasonDetails } from "components/NotificationBanner/utils/errorReasons";
import { format } from "date-fns";
import { closeAccount } from "queries/customer";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { setNotificationBanner } from "reducers/notificationBanner";
import { useAppDispatch, useAppSelector } from "reduxUtils/hook";
import {
  NotificationBannerNames,
  NotificationBannerTypes,
} from "utils/constants";
import { jwtClient } from "utils/jwtClient";

import { CloseAccountDialog } from "./CloseAccountDialog";
import { useCloseAccountFormContext } from "./CloseAccountPageContext";
import content from "./static/content.json";
const { buttonContent, dateHint, dateInputLabel, dateLabel, title } = content;

export const CloseAccountPageContent: React.FC = () => {
  const { pathname } = useLocation();
  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const { accountNumber } = useAppSelector((store) => store.currentAccount);
  const {
    closedDate: {
      fieldValue: { value: moveOutDateValue, error },
      handleOnChange: setClosedDate,
    },
    isFormValid,
  } = useCloseAccountFormContext();

  const { isLoading, mutate } = useMutation({
    mutationFn: ({
      account,
      moveOutDate,
    }: {
      account: string;
      moveOutDate: string;
    }) => closeAccount(account, jwtClient, moveOutDate),
    onSuccess: () => {
      dispatch(
        setNotificationBanner({
          type: NotificationBannerTypes.notice,
          name: NotificationBannerNames.accountClosure,
          params: {
            closedAccountDate: format(new Date(moveOutDateValue), "dd/MM/yy"),
          },
        }),
      );
    },
    onError: (err: Error) => {
      dispatch(
        setNotificationBanner({
          type: NotificationBannerTypes.error,
          name: NotificationBannerNames.accountClosure,
          page: pathname,
          additionalContent: errorReasonDetails(err),
        }),
      );
    },
  });

  const handleDateChange = (value: string) => setClosedDate(value);

  const handleDialog = () => {
    setDialogIsOpen((prev) => !prev);
  };

  const confirmCloseAccount = () => {
    setDialogIsOpen(false);
    mutate({ account: accountNumber, moveOutDate: moveOutDateValue });
  };

  return (
    <>
      <div>
        <h4 className="heading_subpage">{title}</h4>
        <p className="gotham_bold">{dateLabel}</p>
        <DateInput
          errorMessage={error}
          handleDateChange={handleDateChange}
          hint={dateHint}
          label={dateInputLabel}
          name="closeAccountDate"
          required
          value={moveOutDateValue}
        />
        {isLoading ? (
          <LoadingSection cssClass="spacer spacer--large" />
        ) : (
          <button
            className="button button_primary--red button_primary--small button--expanded spacer spacer--large"
            disabled={!isFormValid}
            onClick={handleDialog}
            type="submit"
          >
            {buttonContent}
          </button>
        )}
      </div>
      <Dialog handleDialogClose={handleDialog} dialogIsHidden={!dialogIsOpen}>
        <CloseAccountDialog
          closeDialog={handleDialog}
          confirmCloseAccount={confirmCloseAccount}
        />
      </Dialog>
    </>
  );
};
